import { useState, useEffect, useCallback } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Search,
  Filter,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  FileText,
  Trash2,
  RefreshCw,
  Plus,
  Receipt,
  AlertTriangle,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/components/ui/Pagination';
import { DetailField } from '@/components/common/DetailSection';
import { FormField } from '@/components/common/FormSection';
import { AmountText, FinanceStatusBadge } from '@/components/finance-v2/FinanceBadges';
import { financeV2Service } from '@/services/financeV2Service';
import { Invoice, InvoiceWithItems } from '@/types/financeV2.types';
import { cn } from '@/utils/cn';

import { getErrorMessage } from '../utils/error.utils'
import { parseDbTimestamp } from '../utils/datetime'
import { NativeSelect } from "@/components/ui/native-select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

type InvoiceStatus = 'OPEN' | 'PAID' | 'CANCELLED' | 'OVERDUE';

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  OPEN: 'Aberta',
  PAID: 'Paga',
  CANCELLED: 'Cancelada',
  OVERDUE: 'Vencida',
};

const ITEM_TYPE_LABELS: Record<string, string> = {
  FEE: 'Honorário',
  CLIENT_EXPENSE: 'Despesa',
};

function InvoiceStatusBadge({ status }: { status: InvoiceStatus }) {
  return <FinanceStatusBadge status={status}>{STATUS_LABELS[status]}</FinanceStatusBadge>;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

function formatDate(value: string | null | undefined) {
  const d = parseDbTimestamp(value);
  return d ? format(d, 'dd/MM/yyyy', { locale: ptBR }) : '-';
}

/** Botão de ação da linha: só ícone, com nome acessível e dica. */
function RowAction({
  label,
  icon: Icon,
  onClick,
  className,
}: {
  label: string;
  icon: ComponentType<{ className?: string }>;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon-sm" onClick={onClick} aria-label={label} className={className}>
          <Icon className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function TotalLine({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex justify-between gap-4 text-sm', className)}>
      <span className="text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

export function FinanceV2InvoicesPage() {
  const navigate = useNavigate();

  // State
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: '' as InvoiceStatus | '',
    clientId: '',
    from_date: '',
    to_date: '',
  });

  // Modals
  const [showFilters, setShowFilters] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceWithItems | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showDismantleModal, setShowDismantleModal] = useState(false);

  // Pay modal data
  const [payData, setPayData] = useState({
    paid_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: '',
    notes: '',
  });

  // Dismantle modal data
  const [dismantleData, setDismantleData] = useState({
    cancel_transactions: false,
    reason: '',
  });

  // Load invoices
  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: Record<string, unknown> = {
        page,
        limit: pagination.limit,
      };

      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.clientId) params.client_id = filters.clientId;
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;

      const response = await financeV2Service.invoices.list(params);

      setInvoices(response.data.invoices);
      setPagination(prev => ({ ...prev, ...response.data.pagination }));
    } catch (err: unknown) {
      console.error('Error loading invoices:', err);
      setError(getErrorMessage(err, 'Erro ao carregar faturas'));
    } finally {
      setLoading(false);
    }
  }, [page, filters, pagination.limit]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // View invoice details
  const handleViewDetails = async (invoiceId: string) => {
    try {
      setLoading(true);
      const response = await financeV2Service.invoices.getById(invoiceId);
      setSelectedInvoice(response.data.invoice);
      setShowDetailsModal(true);
    } catch (err: unknown) {
      console.error('Error loading invoice details:', err);
      alert(getErrorMessage(err, 'Erro ao carregar detalhes da fatura'));
    } finally {
      setLoading(false);
    }
  };

  // Pay invoice
  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvoice) return;

    try {
      setLoading(true);
      await financeV2Service.invoices.pay(selectedInvoice.id, payData);
      alert('Fatura paga com sucesso!');
      setShowPayModal(false);
      setSelectedInvoice(null);
      loadInvoices();
    } catch (err: unknown) {
      console.error('Error paying invoice:', err);
      alert(getErrorMessage(err, 'Erro ao pagar fatura'));
    } finally {
      setLoading(false);
    }
  };

  // Issue boleto
  const handleIssueBoleto = async (invoiceId: string) => {
    const accountId = prompt('Digite o ID da conta BTG:');
    if (!accountId) return;

    try {
      setLoading(true);
      await financeV2Service.invoices.issueBoleto(invoiceId, { account_id: accountId });
      alert('Boleto emitido com sucesso!');
      loadInvoices();
    } catch (err: unknown) {
      console.error('Error issuing boleto:', err);
      alert(getErrorMessage(err, 'Erro ao emitir boleto'));
    } finally {
      setLoading(false);
    }
  };

  // Dismantle invoice
  const handleDismantle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvoice) return;

    try {
      setLoading(true);
      await financeV2Service.invoices.dismantle(selectedInvoice.id, dismantleData);
      alert('Fatura desmontada com sucesso!');
      setShowDismantleModal(false);
      setSelectedInvoice(null);
      loadInvoices();
    } catch (err: unknown) {
      console.error('Error dismantling invoice:', err);
      alert(getErrorMessage(err, 'Erro ao desmontar fatura'));
    } finally {
      setLoading(false);
    }
  };

  // Delete invoice
  const handleDelete = async (invoiceId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta fatura?')) return;

    try {
      setLoading(true);
      await financeV2Service.invoices.delete(invoiceId);
      alert('Fatura excluída com sucesso!');
      loadInvoices();
    } catch (err: unknown) {
      console.error('Error deleting invoice:', err);
      alert(getErrorMessage(err, 'Erro ao excluir fatura'));
    } finally {
      setLoading(false);
    }
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedInvoice(null);
  };

  const closePayModal = () => {
    setShowPayModal(false);
    setSelectedInvoice(null);
  };

  const closeDismantleModal = () => {
    setShowDismantleModal(false);
    setSelectedInvoice(null);
  };

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <Receipt className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Faturas</h1>
          </div>
          <p className="text-sm text-muted-foreground">Gerenciamento de faturas de clientes</p>
        </div>
        <Button onClick={() => navigate('/finance-v2/invoices/to-invoice')}>
          <Plus className="size-4" /> Nova Fatura
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              type="text"
              aria-label="Buscar faturas"
              placeholder="Buscar por número, cliente..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-8"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <NativeSelect
              aria-label="Filtrar por status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value as InvoiceStatus | '' })}
            >
              <option value="">Todos os status</option>
              <option value="OPEN">Aberta</option>
              <option value="PAID">Paga</option>
              <option value="OVERDUE">Vencida</option>
              <option value="CANCELLED">Cancelada</option>
            </NativeSelect>

            <Button variant="outline" onClick={() => setShowFilters(!showFilters)} aria-expanded={showFilters}>
              <Filter className="size-4" />
              Filtros
            </Button>

            <Button variant="outline" size="icon" onClick={loadInvoices} disabled={loading} aria-label="Atualizar">
              <RefreshCw className={cn('size-4', loading && 'animate-spin')} />
            </Button>
          </div>
        </div>

        {showFilters && (
          <Card size="sm">
            <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Data Inicial" htmlFor="invoices-filter-from">
                <Input
                  id="invoices-filter-from"
                  type="date"
                  value={filters.from_date}
                  onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                />
              </FormField>
              <FormField label="Data Final" htmlFor="invoices-filter-to">
                <Input
                  id="invoices-filter-to"
                  type="date"
                  value={filters.to_date}
                  onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                />
              </FormField>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Error */}
      {error && (
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Table */}
      {loading && invoices.length === 0 ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          <span className="sr-only">Carregando faturas...</span>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14" />)}
        </div>
      ) : invoices.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <Receipt className="size-10 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-sm font-medium">Nenhuma fatura encontrada</h3>
          </CardContent>
        </Card>
      ) : (
        <Card className="min-w-0 py-0">
          <div className="min-w-0 overflow-x-auto p-2">
            <Table className="min-w-[880px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Número</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Emissão</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>
                      <div className="font-medium">{invoice.invoice_number}</div>
                      {invoice.boleto_number && (
                        <div className="text-xs text-muted-foreground">Boleto: {invoice.boleto_number}</div>
                      )}
                    </TableCell>
                    <TableCell>{invoice.client_name || 'N/A'}</TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">{formatDate(invoice.issue_date)}</TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">{formatDate(invoice.due_date)}</TableCell>
                    <TableCell>
                      <AmountText tone="neutral">{formatCurrency(invoice.total)}</AmountText>
                      {invoice.discount > 0 && (
                        <div className="text-xs text-muted-foreground">Desc: {formatCurrency(invoice.discount)}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <InvoiceStatusBadge status={invoice.status} />
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <RowAction label="Ver detalhes" icon={Eye} onClick={() => handleViewDetails(invoice.id)} />

                        {invoice.status === 'OPEN' && (
                          <RowAction
                            label="Pagar"
                            icon={CheckCircle}
                            className="text-primary"
                            onClick={() => {
                              setSelectedInvoice(invoice as InvoiceWithItems);
                              setShowPayModal(true);
                            }}
                          />
                        )}

                        {invoice.status === 'OPEN' && !invoice.boleto_id && (
                          <RowAction label="Emitir boleto" icon={FileText} onClick={() => handleIssueBoleto(invoice.id)} />
                        )}

                        {/* Download boleto - <a> tag, manter title */}
                        {invoice.boleto_url && (
                          <Button variant="ghost" size="icon-sm" asChild>
                            <a
                              href={invoice.boleto_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Baixar boleto"
                              aria-label="Baixar boleto"
                            >
                              <Download className="size-4" />
                            </a>
                          </Button>
                        )}

                        {invoice.status === 'OPEN' && (
                          <RowAction
                            label="Desmontar"
                            icon={XCircle}
                            onClick={() => {
                              setSelectedInvoice(invoice as InvoiceWithItems);
                              setShowDismantleModal(true);
                            }}
                          />
                        )}

                        <RowAction
                          label="Excluir"
                          icon={Trash2}
                          className="text-destructive"
                          onClick={() => handleDelete(invoice.id)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="border-t border-border p-3">
              <Pagination
                currentPage={page}
                totalPages={pagination.totalPages}
                onPageChange={setPage}
                pageSize={pagination.limit}
                totalItems={pagination.total}
              />
            </div>
          )}
        </Card>
      )}

      {/* Details Modal */}
      <Dialog open={showDetailsModal && !!selectedInvoice} onOpenChange={(open) => { if (!open) closeDetailsModal() }}>
        {selectedInvoice && (
          <DialogContent size="xl" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Fatura {selectedInvoice.invoice_number}</DialogTitle>
              <DialogDescription>{selectedInvoice.client_name}</DialogDescription>
            </DialogHeader>

            <div className="flex min-w-0 flex-col gap-6">
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailField label="Data de Emissão">{formatDate(selectedInvoice.issue_date)}</DetailField>
                <DetailField label="Data de Vencimento">{formatDate(selectedInvoice.due_date)}</DetailField>
                {selectedInvoice.paid_date && (
                  <DetailField label="Data de Pagamento">{formatDate(selectedInvoice.paid_date)}</DetailField>
                )}
                <DetailField label="Status">
                  <InvoiceStatusBadge status={selectedInvoice.status} />
                </DetailField>
              </dl>

              <div className="flex min-w-0 flex-col gap-2">
                <h3 className="text-sm font-medium">Itens</h3>
                <div className="min-w-0 overflow-x-auto rounded-lg border border-border">
                  <Table className="min-w-[560px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Descrição</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead className="text-right">Qtd</TableHead>
                        <TableHead className="text-right">Valor Unit.</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedInvoice.items?.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="whitespace-normal break-words">{item.description}</TableCell>
                          <TableCell className="text-muted-foreground">{ITEM_TYPE_LABELS[item.item_type] ?? 'Timesheet'}</TableCell>
                          <TableCell className="text-right tabular-nums">{item.quantity}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell className="text-right">
                            <AmountText tone="neutral">{formatCurrency(item.total)}</AmountText>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="flex justify-end border-t border-border pt-4">
                <div className="flex w-full flex-col gap-2 sm:w-64">
                  <TotalLine label="Subtotal:">
                    <AmountText tone="neutral">{formatCurrency(selectedInvoice.subtotal)}</AmountText>
                  </TotalLine>
                  {selectedInvoice.discount > 0 && (
                    <TotalLine label="Desconto:">
                      <AmountText tone="expense">-{formatCurrency(selectedInvoice.discount)}</AmountText>
                    </TotalLine>
                  )}
                  <div className="flex justify-between gap-4 border-t border-border pt-2 text-base font-semibold">
                    <span>Total:</span>
                    <span className="tabular-nums">{formatCurrency(selectedInvoice.total)}</span>
                  </div>
                </div>
              </div>

              {(selectedInvoice.notes || selectedInvoice.payment_instructions) && (
                <dl className="flex flex-col gap-4">
                  {selectedInvoice.notes && (
                    <DetailField label="Observações">{selectedInvoice.notes}</DetailField>
                  )}
                  {selectedInvoice.payment_instructions && (
                    <DetailField label="Instruções de Pagamento">{selectedInvoice.payment_instructions}</DetailField>
                  )}
                </dl>
              )}
            </div>
          </DialogContent>
        )}
      </Dialog>

      {/* Pay Modal */}
      <Dialog open={showPayModal && !!selectedInvoice} onOpenChange={(open) => { if (!open && !loading) closePayModal() }}>
        {selectedInvoice && (
          <DialogContent size="sm" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Pagar Fatura {selectedInvoice.invoice_number}</DialogTitle>
              <DialogDescription>Registre a data e a forma do pagamento recebido.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handlePay} className="flex min-w-0 flex-col gap-4">
              <FormField label="Data de Pagamento *" htmlFor="invoice-pay-date">
                <Input
                  id="invoice-pay-date"
                  type="date"
                  required
                  value={payData.paid_date}
                  onChange={(e) => setPayData({ ...payData, paid_date: e.target.value })}
                />
              </FormField>

              <FormField label="Método de Pagamento" htmlFor="invoice-pay-method">
                <Input
                  id="invoice-pay-method"
                  type="text"
                  value={payData.payment_method}
                  onChange={(e) => setPayData({ ...payData, payment_method: e.target.value })}
                  placeholder="Ex: Transferência, PIX, Boleto"
                />
              </FormField>

              <FormField label="Observações" htmlFor="invoice-pay-notes">
                <Textarea
                  id="invoice-pay-notes"
                  value={payData.notes}
                  onChange={(e) => setPayData({ ...payData, notes: e.target.value })}
                  rows={3}
                />
              </FormField>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={closePayModal}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Processando...' : 'Confirmar Pagamento'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        )}
      </Dialog>

      {/* Dismantle Modal */}
      <Dialog open={showDismantleModal && !!selectedInvoice} onOpenChange={(open) => { if (!open && !loading) closeDismantleModal() }}>
        {selectedInvoice && (
          <DialogContent size="sm" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Desmontar Fatura {selectedInvoice.invoice_number}</DialogTitle>
              <DialogDescription>Os itens voltam para a lista de itens a faturar.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleDismantle} className="flex min-w-0 flex-col gap-4">
              <div className="flex gap-2 rounded-lg border border-border bg-muted/50 p-3 text-sm">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <p>
                  Esta ação irá desfazer a fatura, removendo os itens e restaurando as transações/timesheets para o estado &quot;a faturar&quot;.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="cancel_transactions"
                  checked={dismantleData.cancel_transactions}
                  onChange={(e) => setDismantleData({ ...dismantleData, cancel_transactions: e.target.checked })}
                  className="size-4 rounded-sm border-input accent-primary"
                />
                <Label htmlFor="cancel_transactions" className="font-normal">
                  Cancelar também as transações associadas
                </Label>
              </div>

              <FormField label="Motivo" htmlFor="invoice-dismantle-reason">
                <Textarea
                  id="invoice-dismantle-reason"
                  value={dismantleData.reason}
                  onChange={(e) => setDismantleData({ ...dismantleData, reason: e.target.value })}
                  rows={3}
                  placeholder="Informe o motivo do desmonte da fatura"
                />
              </FormField>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeDismantleModal}>
                  Cancelar
                </Button>
                <Button type="submit" variant="destructive" disabled={loading}>
                  {loading ? 'Processando...' : 'Desmontar Fatura'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
